import { RelativeOrientationConstraint, CyclicOrientationConstraint, AlignConstraint, GroupBySelector } from "./layoutspec";
import { EdgeStyle } from "./edge-style";
import type { TextStyle } from "./style/text-style";
import type { IconPlacement } from "./style/atom-style-spec";
import type { LayoutWarning } from "./error-state";

export interface LayoutGroup {
    // The internal name of the group, used for identity and edge attachments.
    name : string;
    /** Display caption. Absent on older/custom layouts: render `name`. */
    label?: string;

    // The nodes that are in the group
    nodeIds : string[];

    // The key node of the group
    keyNodeId : string;

    // Show label
    showLabel : boolean;

    // Styling for the group's own label, from the group directive's `textStyle`.
    // Only `color` is consumed today; `size` is deferred (group labels auto-fit).
    labelTextStyle?: TextStyle;

    // The group constraint that created this group
    sourceConstraint?: GroupBySelector;

    // If true, this is a negated group: "no clean rectangle can contain exactly these members."
    // Negated groups don't draw a visual rectangle; they generate anti-containment disjunctions.
    negated?: boolean;

    // If true, this group overlaps (shares nodes with) another group without subsumption.
    // Shared nodes appear as leaves in both groups; WebCola handles bounds via its VPSC solver.
    overlapping?: boolean;

    /**
     * True when the group came from a binary group-by-selector, i.e. keyNodeId
     * is a real key that identifies this group within its constraint's family.
     * Unary-selector groups set keyNodeId to an arbitrary member, so they are
     * not addressable by (name, key) — inferredEdge `draw` endpoints resolve
     * only against keyed groups.
     */
    keyed?: boolean;
}

/**
 * Where a node's resolved `color` came from. The renderer keys display
 * decisions off this — e.g. it may re-tune an algorithm-assigned color for a
 * themed canvas but must preserve a user-chosen one. Modeled as an enum (not a
 * boolean) so new origins — inherited, highlight, a future colorscheme
 * directive — can be added without reshaping {@link LayoutNode} or migrating
 * call sites. New variants default to non-themeable until the renderer opts
 * them in (see WebColaCnDGraph.isThemeableNodeColor).
 */
export enum ColorSource {
    /**
     * Assigned by the default type-palette algorithm (the phyllotactic picker)
     * or the black fallback. Safe for the renderer to re-tint per theme.
     */
    DefaultPalette = 'default-palette',
    /** Set by an explicit user `color` directive — preserved exactly as chosen. */
    Directive = 'directive'
}

export interface LayoutNode {
    /** Host opt-in to font remeasurement for custom nodes. Generated automatic
     * boxes are tracked internally; explicit size constraints remain exact. */
    autoSize?: boolean;
    id: string;
    label: string;
    /**
     * The node's border/outline color. Historically the node's single "color"
     * (from `atomColor`/`color`), which the renderer draws as the rectangle
     * stroke; `atomStyle.borderStyle.color` now feeds it too.
     */
    color : string;
    /**
     * Provenance of `color` (see {@link ColorSource}). Lets the renderer retune
     * algorithm-assigned colors for a themed canvas while leaving user-chosen
     * colors untouched. Absent is treated as {@link ColorSource.DefaultPalette}.
     */
    colorSource?: ColorSource;
    /**
     * Interior fill of the node's rectangle, from `atomStyle.fillStyle.color`.
     * Absent = the renderer's default (a Tufte canvas-matched fill, so only the
     * stroke + label mark the node). A real fill is opt-in.
     */
    fillColor?: string;
    /**
     * Border/stroke width in px, from `atomStyle.borderStyle.width`. Absent = the
     * renderer's default node stroke width.
     */
    borderWidth?: number;
    /**
     * Styling for the node's main (name) label, from `atomStyle.textStyle`.
     * Absent = default. NOTE: only `color` is consumed today; `size` is deferred
     * because the box sizer (estimateLabelBox) fixes the main label at
     * MAIN_LABEL_FONT_SIZE — changing it must also re-flow the box.
     */
    textStyle?: TextStyle;
    groups?: string[];
    attributes?: Record<string, string[]>;
    /**
     * Per-attribute-key text style (the shared `textStyle` block: size + color)
     * from an `attribute` or `tag` directive. Keyed by the same attribute key as
     * {@link attributes}. Absent keys render at the normal secondary size with
     * the inherited label color.
     */
    attributeTextStyles?: Record<string, TextStyle>;
    /**
     * Labels associated with this node from the data instance (e.g., Skolems).
     * These are displayed prominently on nodes, typically styled in the node's color.
     * Unlike attributes (which come from field relationships), labels come from
     * metadata in the data instance.
     */
    labels?: Record<string, string[]>;
    /** Resolved icon URL / data URI from `atomStyle.iconStyle.path`. Empty = no icon. */
    icon? : string;
    /**
     * How {@link icon} draws: `full` (occupies the box, which stays transparent
     * unless an explicit fill was asked for) or `badge` (small top-right marker).
     * Defaults to `full`. Meaningless without an {@link icon}.
     */
    iconPlacement? : IconPlacement;
    /** Icon alpha in [0,1] from `atomStyle.iconStyle.opacity`. Undefined = opaque. */
    iconOpacity? : number;
    width : number;
    height : number;
    mostSpecificType : string;
    types : string[];
    /**
     * Whether the atom's label is drawn, from `atomStyle.showLabel` (default true).
     * Named plural for back-compat with the render contract; the spec-level key is
     * the singular `showLabel`, matching `edgeStyle`.
     */
    showLabels : boolean;
    /** True when the node has no edges connecting it to the rest of the graph. */
    disconnected?: boolean;
}


export interface LayoutEdge {
    source: LayoutNode;
    target: LayoutNode;
    label: string;
    relationName : string;
    id : string;
    color: string;
    style?: EdgeStyle;
    weight?: number;
    showLabel?: boolean;
    hidden?: boolean;
    /** Highlight color rendered as a wider underlay beneath the edge. Undefined = no highlight. */
    highlight?: string;
    /** Optional label styling (size / color), applied to the edge's label text. */
    textStyle?: TextStyle;
    /**
     * For group edges (_g_ prefix), the name of the group this edge was created
     * for (matches `group.id` in the WebCola translator). INFORMATIONAL since
     * the per-end stamp unification — the renderer routes exclusively off
     * {@link sourceGroupId}/{@link targetGroupId}; consumers (e.g. the explorer)
     * still use this to identify connector edges.
     */
    groupId?: string;
    /**
     * For group edges (_g_ prefix), the node ID of the key (groupOn) node — the
     * external anchor. INFORMATIONAL since the per-end stamp unification: the
     * renderer no longer derives the snap side from it.
     */
    keyNodeId?: string;
    /**
     * The group (by name, = `group.id` in the WebCola translator) whose hull the
     * SOURCE end attaches to. The renderer's single group-edge contract: set by
     * inferredEdge `draw` endpoints and by the `addEdge` connector desugar
     * ('fromgroup' connectors stamp this side). The edge's `source` node is then
     * just the anchor (a member of that group), not the visual endpoint.
     * Undefined = the source end renders at the node itself.
     */
    sourceGroupId?: string;
    /**
     * Same as {@link sourceGroupId}, for the TARGET end ('togroup' connectors
     * stamp this side).
     */
    targetGroupId?: string;
}

export class ImplicitConstraint {
    constructor(public c : RelativeOrientationConstraint | CyclicOrientationConstraint, public reason: string) {}

    toHTML(): string {
        const origHTML = this.c.toHTML();
        return `Implicit constraint ${origHTML} because ${this.reason}`;
    }
}

export interface LayoutConstraint {
    sourceConstraint: RelativeOrientationConstraint | CyclicOrientationConstraint | AlignConstraint | ImplicitConstraint | GroupBySelector;
}


export interface TopConstraint extends LayoutConstraint {
    top : LayoutNode;
    bottom : LayoutNode;
    minDistance : number;
}

// Add a typeguard for the constraint
export function isTopConstraint(constraint: LayoutConstraint): constraint is TopConstraint {
    return (constraint as TopConstraint).top !== undefined;
}

export interface LeftConstraint extends LayoutConstraint {
    left : LayoutNode;
    right : LayoutNode;
    minDistance : number;
}

export function isLeftConstraint(constraint: LayoutConstraint): constraint is LeftConstraint {
    return (constraint as LeftConstraint).left !== undefined;
}

// Same value along axis
export interface AlignmentConstraint extends LayoutConstraint {
    axis : "x" | "y";
    node1 : LayoutNode;
    node2 : LayoutNode;
}

export function isAlignmentConstraint(constraint: LayoutConstraint): constraint is AlignmentConstraint {
    return (constraint as AlignmentConstraint).axis !== undefined;
}

/**
 * Represents a bounding box constraint for a group.
 * The bounding box has 4 variables (left, right, top, bottom).
 * This is used in disjunctions to enforce that non-members are outside the group.
 * 
 * The actual Kiwi constraint generation happens in the solver - this is just the high-level representation.
 */
export interface BoundingBoxConstraint extends LayoutConstraint {
    /** The group whose bounding box this represents */
    group: LayoutGroup;
    /** The node that must be positioned relative to the bounding box */
    node: LayoutNode;
    /** Which side of the bounding box: 'left' | 'right' | 'top' | 'bottom' */
    side: 'left' | 'right' | 'top' | 'bottom';
    /** Minimum padding from the bounding box edge */
    minDistance: number;
}

export function isBoundingBoxConstraint(constraint: LayoutConstraint): constraint is BoundingBoxConstraint {
    return (constraint as BoundingBoxConstraint).group !== undefined && 
           (constraint as BoundingBoxConstraint).side !== undefined;
}

/**
 * Constraint representing group-to-group boundary separation.
 * Used in disjunctive constraints to ensure two groups are positioned in one of four ways:
 * - groupA left of groupB
 * - groupA right of groupB
 * - groupA above groupB
 * - groupA below groupB
 */
export interface GroupBoundaryConstraint extends LayoutConstraint {
    /** First group */
    groupA: LayoutGroup;
    /** Second group */
    groupB: LayoutGroup;
    /** Which separation: 'left' (A left of B) | 'right' (A right of B) | 'top' (A above B) | 'bottom' (A below B) */
    side: 'left' | 'right' | 'top' | 'bottom';
    /** Minimum padding between group boundaries */
    minDistance: number;
}

export function isGroupBoundaryConstraint(constraint: LayoutConstraint): constraint is GroupBoundaryConstraint {
    return (constraint as GroupBoundaryConstraint).groupA !== undefined && 
           (constraint as GroupBoundaryConstraint).groupB !== undefined;
}

export interface InstanceLayout {
    nodes: LayoutNode[];
    edges: LayoutEdge[];
    constraints: LayoutConstraint[]; // Conjunctive constraints that must always be satisfied
    groups: LayoutGroup[];
    conflictingConstraints?: LayoutConstraint[];
    overlappingNodes?: LayoutNode[]; // IDs of overlapping nodes
    /**
     * Advisory notes about constraints and directives that quietly had no effect —
     * a selector naming something absent from the instance, say.
     *
     * Rides on the layout, like `conflictingConstraints` and `overlappingNodes`,
     * for the same reason: every host calls `generateLayout` itself and then hands
     * the diagram element only the layout. Carrying warnings here means the
     * element can surface them without any host wiring.
     */
    warnings?: LayoutWarning[];
    /**
     * Atoms hidden by a hideAtom directive that a layout constraint references. The hide
     * and the constraint cannot both be satisfied (the layout is reported unsat via
     * HiddenNodeConflictError); this counterfactual diagram shows the atoms anyway, and
     * the renderer marks them distinctly (dashed outline) as hidden-but-shown.
     */
    reintroducedNodes?: LayoutNode[];
    /**
     * IDs of atoms a hideAtom constraint removed from this layout, sorted. Absent
     * when no atom was hidden by selector. Legacy disconnected-node hiding
     * (hideDisconnected / hideDisconnectedBuiltIns) is not recorded here: this
     * field answers "what did the spec's hideAtom constraints hide", which is
     * what the `hidden()` spatial query reports.
     */
    hiddenAtoms?: string[];
    /**
     * Node-id fragments selected by non-negated cyclic constraints, one entry
     * per fragment, in selector order (reversed for counterclockwise). Absent
     * when no cyclic constraint selected anything. This is what the `cyclic()`
     * spatial query reports: membership is settled by selection, so a two-atom
     * fragment counts even though drawing it needs no disjunction. Singleton
     * fragments cycle with nobody and are not recorded.
     */
    cyclicFragments?: string[][];
    /**
     * Disjunctive constraints, where at least one alternative in each disjunction must be satisfiable.
     * These are separate from conjunctive constraints for clearer solver integration.
     */
    disjunctiveConstraints?: DisjunctiveConstraint[];

    // TODO: One frustration in the instance layout is that really this is not ..quite.. what the
    // constraint validator should take. Its not a ``validator`` per se, its more like a ``refiner``
    // that takes an instance layout and produces a refined instance layout with more constraints, but also
    // validates if needed.

    // Perhaps there is another intermediate type here that is like a ``RefinableInstanceLayout`` that has
    // some of these extra fields, and then the constraint validator takes that and produces an InstanceLayout?
}

// Can we write a typeguard for this?
export function isInstanceLayout(obj: any): obj is InstanceLayout {
    return (
        Array.isArray(obj.nodes) &&
        Array.isArray(obj.edges) &&
        Array.isArray(obj.constraints) &&
        Array.isArray(obj.groups) &&
        obj.nodes.every((node: any) => typeof node.id === 'string') &&
        obj.edges.every((edge: any) => typeof edge.source === 'object' && typeof edge.target === 'object') &&
        obj.constraints.every((constraint: any) => typeof constraint.sourceConstraint === 'object') &&
        obj.groups.every((group: any) => typeof group.name === 'string') &&
        (obj.disjunctiveConstraints === undefined || Array.isArray(obj.disjunctiveConstraints))
    );
}


/**
 * Represents a disjunctive constraint, where at least one of the provided alternatives must be satisfiable.
 * Each alternative is an array of layout constraints that must hold together if selected.
 * Used primarily for cyclic constraints, where alternatives represent different perturbations (rotations) of a cycle.
 */
export class DisjunctiveConstraint {
    /**
     * Creates a new disjunctive constraint.
     * @param sourceConstraint - The original constraint (e.g., CyclicOrientationConstraint, GroupBySelector, or ImplicitConstraint) that led to this disjunction.
     * @param alternatives - An array of alternatives, where each alternative is an array of constraints that must be satisfied together.
     */
    constructor(
        public sourceConstraint:  CyclicOrientationConstraint | GroupBySelector | ImplicitConstraint | AlignConstraint | RelativeOrientationConstraint,
        public alternatives: LayoutConstraint[][]
    ) {}

    /**
     * Returns a string representation of the disjunctive constraint for debugging.
     */
    toString(): string {
        return `DisjunctiveConstraint with ${this.alternatives.length} alternatives from ${this.sourceConstraint}`;
    }

    /**
     * Add an alternative to the disjunctive constraint.
     * @param alternative - An array of layout constraints that form a new alternative.
     */
    addAlternative(alternative: LayoutConstraint[]) {
        this.alternatives.push(alternative);
    }

    //TODO: Should we have some simplification methods here? 
}

/**
 * Type guard to check if a constraint is a disjunctive constraint.
 * @param constraint - The constraint to check.
 * @returns True if the constraint is a DisjunctiveConstraint instance.
 */
export function isDisjunctiveConstraint(constraint: LayoutConstraint | DisjunctiveConstraint): constraint is DisjunctiveConstraint {
    return constraint instanceof DisjunctiveConstraint;
}


// ==================== Constraint Negation Utilities ====================
//
// NOT is implemented as a reversed inequality with minDistance=0:
//   NOT TopConstraint(A, B, D) → TopConstraint(B, A, 0)
//   NOT LeftConstraint(A, B, D) → LeftConstraint(B, A, 0)
//   NOT AlignmentConstraint(A, B, axis) → disjunction: must differ on axis
//
// For compound constraints, De Morgan's law applies:
//   NOT(C1 ∧ C2 ∧ ...) = ¬C1 ∨ ¬C2 ∨ ...   (negateConjunction)
//   NOT(A1 ∨ A2 ∨ ...) = ¬A1 ∧ ¬A2 ∧ ...   (negateDisjunction)

/**
 * Negates a single atomic LayoutConstraint.
 *
 * For ordering constraints (Top/Left), negation flips the direction
 * and sets minDistance=0, expressing the reversed ≤ inequality.
 * e.g. NOT(A above B) = A.y ≤ B.y = TopConstraint(B, A, 0).
 *
 * For alignment constraints, negation produces a disjunction (the two
 * nodes must differ on the aligned axis), returned as multiple alternatives.
 *
 * @returns An array of alternatives. Each alternative is a LayoutConstraint[].
 *          For ordering constraints this is [[reversed],[aligned]], for alignment [[alt1],[alt2]].
 */
export function negateAtomicConstraint(
    constraint: LayoutConstraint,
    sourceConstraint: LayoutConstraint['sourceConstraint']
): LayoutConstraint[][] {
    if (isTopConstraint(constraint)) {
        // ¬(top < bottom on y) = (bottom < top on y) ∨ (top ≡ bottom on y)
        const reversed: TopConstraint = {
            top: constraint.bottom,
            bottom: constraint.top,
            minDistance: 0,
            sourceConstraint
        };
        const aligned: AlignmentConstraint = {
            axis: 'y',
            node1: constraint.top,
            node2: constraint.bottom,
            sourceConstraint
        };
        return [[reversed], [aligned]];
    }

    if (isLeftConstraint(constraint)) {
        // ¬(left < right on x) = (right < left on x) ∨ (left ≡ right on x)
        const reversed: LeftConstraint = {
            left: constraint.right,
            right: constraint.left,
            minDistance: 0,
            sourceConstraint
        };
        const aligned: AlignmentConstraint = {
            axis: 'x',
            node1: constraint.left,
            node2: constraint.right,
            sourceConstraint
        };
        return [[reversed], [aligned]];
    }

    if (isAlignmentConstraint(constraint)) {
        // NOT (same axis) → must differ on that axis → disjunction
        if (constraint.axis === 'y') {
            // NOT same-Y → node1 above node2 OR node2 above node1
            const alt1: TopConstraint = {
                top: constraint.node1, bottom: constraint.node2,
                minDistance: 1, sourceConstraint
            };
            const alt2: TopConstraint = {
                top: constraint.node2, bottom: constraint.node1,
                minDistance: 1, sourceConstraint
            };
            return [[alt1], [alt2]];
        } else {
            // NOT same-X → node1 left-of node2 OR node2 left-of node1
            const alt1: LeftConstraint = {
                left: constraint.node1, right: constraint.node2,
                minDistance: 1, sourceConstraint
            };
            const alt2: LeftConstraint = {
                left: constraint.node2, right: constraint.node1,
                minDistance: 1, sourceConstraint
            };
            return [[alt1], [alt2]];
        }
    }

    // Fallback: return original unchanged (BoundingBox, GroupBoundary — not yet supported)
    return [[constraint]];
}

/**
 * Negates a conjunction of constraints using De Morgan's law.
 * NOT(C1 ∧ C2 ∧ ... ∧ Cn) = ¬C1 ∨ ¬C2 ∨ ... ∨ ¬Cn
 *
 * Each ¬Ci may itself produce multiple alternatives (e.g. negated alignment).
 * All are flattened into a single set of alternatives for a DisjunctiveConstraint.
 */
export function negateConjunction(
    conjunction: LayoutConstraint[],
    sourceConstraint: LayoutConstraint['sourceConstraint']
): LayoutConstraint[][] {
    const alternatives: LayoutConstraint[][] = [];
    for (const c of conjunction) {
        const negatedAlts = negateAtomicConstraint(c, sourceConstraint);
        alternatives.push(...negatedAlts);
    }
    return alternatives;
}

/**
 * Negates a DisjunctiveConstraint using De Morgan's law.
 * NOT(A1 ∨ A2 ∨ ... ∨ An) = ¬A1 ∧ ¬A2 ∧ ... ∧ ¬An
 *
 * Each ¬Ai = negateConjunction(Ai) produces a new DisjunctiveConstraint.
 * The returned array of DisjunctiveConstraints is implicitly conjunctive
 * (all must be satisfied), matching the existing solver contract.
 */
export function negateDisjunction(
    disjunction: DisjunctiveConstraint,
    sourceConstraint: DisjunctiveConstraint['sourceConstraint']
): DisjunctiveConstraint[] {
    const result: DisjunctiveConstraint[] = [];
    for (const alternative of disjunction.alternatives) {
        const negatedAlternatives = negateConjunction(alternative, sourceConstraint);
        result.push(new DisjunctiveConstraint(sourceConstraint, negatedAlternatives));
    }
    return result;
}
